import {DateTime} from 'luxon';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {AnalyticsReportsEnum} from '../../../analytics/helpers/analyticsReportHelpers';
import {parseInfoFromLocation} from '../../../analytics/helpers/analyticsUrlHelpers';
import {AppRoutesPrefixesEnum} from '../../../components/appRoutesPrefixes';
import {computeIsGlobalNamespace} from '../../../helpers/models/namespaceHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {TeamModel} from '../../../models/api/teamModel';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken} from '../../../services/panelsCancellers';
import {AppState} from '../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getIsUserGlobalAdmin, getUserNamespace} from '../../data/teammates/teammatesSelectors';
import {generateNavigationBlockerId} from '../app/appPanelHelpers';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {getPanels} from '../panelsSelectors';
import {AnalyticsPanelId, PanelTypesEnum} from '../panelStateBase';
import {getTeamsAccessibleForAnalytics} from './analyticsPanelSelectors';
import {AnalyticsPanelDetailsAction} from './details/analyticsPanelDetailsActions';
import {makeAnalyticsPanelDetailsState} from './details/analyticsPanelDetailsReducer';
import {AnalyticsPanelExportsAction} from './exports/analyticsPanelExportsActions';
import {makeAnalyticsPanelExportsState} from './exports/analyticsPanelExportsReducer';
import {AnalyticsPanelFiltersAction} from './filters/analyticsPanelFiltersActions';
import {makeAnalyticsPanelFiltersState} from './filters/analyticsPanelFiltersReducer';
import {
  AnalyticsLocation,
  AnalyticsParameters,
  AnalyticsParametersNamespacesEnum,
} from './parameters/analyticsParameters';
import {AnalyticsPanelReportAction} from './report/analyticsPanelReportActions';
import {makeAnalyticsPanelReportState} from './report/analyticsPanelReportReducer';
import {AnalyticsPanelViewsAction} from './views/analyticsPanelViewsActionsTypes';
import {makeAnalyticsPanelViewsState} from './views/analyticsPanelViewsState';

/*
 * Type enum.
 */

export enum AnalyticsPanelActionsEnum {
  NAVIGATED = '@@panels/analytics/NAVIGATED',
  LOADED = '@@panels/analytics/LOADED',
  SET_SELECTED_NAMESPACE = '@@panels/analytics/SET_SELECTED_NAMESPACE',
  UPDATE_LAST_ADDED_FILTER = '@@panels/analytics/filters/UPDATE_LAST_ADDED_FILTER',
}

/*
 * Types.
 */

type AnalyticsPanelActionPayload = PanelActionPayload<AnalyticsPanelId>;

interface AnalyticsNavigatedActionPayload extends AnalyticsPanelActionPayload {
  parameters: AnalyticsParameters;
}

interface AnalyticsNavigatedAction extends ActionWithPayload<AnalyticsNavigatedActionPayload> {
  type: AnalyticsPanelActionsEnum.NAVIGATED;
}

interface LoadAnalyticsActionPayload {
  reportType?: AnalyticsReportsEnum;
}

interface LoadAnalyticsAction extends ActionWithPayload<LoadAnalyticsActionPayload> {
  type: AnalyticsPanelActionsEnum.LOADED;
}

interface SetSelectedNamespaceActionPayload extends PanelActionPayload<AnalyticsPanelId> {
  namespace: string;
}
interface SetSelectedNamespaceAction extends ActionWithPayload<SetSelectedNamespaceActionPayload> {
  type: AnalyticsPanelActionsEnum.SET_SELECTED_NAMESPACE;
}

/*
 * Action creators.
 */

export function navigateAnalyticsAction(
  panelId: AnalyticsPanelId,
  parameters: AnalyticsParameters | undefined,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the analytics panel with this ID.
    const state = getState();
    const panel = getPanels(state)[panelId];
    if (panel && panel.type !== PanelTypesEnum.ANALYTICS) {
      throw new Error('Wrong panel type.');
    }

    // If the panel exists and a location was provided, update it.
    if (panel && parameters) {
      dispatch<AnalyticsNavigatedAction>({
        type: AnalyticsPanelActionsEnum.NAVIGATED,
        payload: {panelId, parameters},
      });
      return;
    }

    // If the panel exists and no location was provided, we're done.
    if (panel) {
      return;
    }

    const [cancelTokenId] = buildPanelCancelToken(panelId);

    const currentParameters = parameters ?? buildDefaultParameters(state, location);
    const unsavedViewNavigationBlockerId = generateNavigationBlockerId();

    dispatch(
      setPanelAction({
        id: panelId,
        type: PanelTypesEnum.ANALYTICS,
        parameters: currentParameters,
        status: AsyncStatusesEnum.LOADING,
        cancelTokenId,
        sequence: findPanelSequence(),

        unsavedViewNavigationBlockerId,

        // Sub-states.
        filters: makeAnalyticsPanelFiltersState(),
        views: makeAnalyticsPanelViewsState(),
        report: makeAnalyticsPanelReportState(),
        details: makeAnalyticsPanelDetailsState(),
        exports: makeAnalyticsPanelExportsState(),
      }),
    );
  };
}

export function setSelectedNamespaceAction(panelId: AnalyticsPanelId, namespace: string): ThunkAsyncAction {
  return async (dispatch) => {
    // Update the store.
    dispatch({
      type: AnalyticsPanelActionsEnum.SET_SELECTED_NAMESPACE,
      payload: {
        panelId,
        namespace,
      },
    });
  };
}

/*
 * Union type.
 */

export type AnalyticsPanelAction =
  | AnalyticsNavigatedAction
  | LoadAnalyticsAction
  | SetSelectedNamespaceAction
  | AnalyticsPanelFiltersAction
  | AnalyticsPanelReportAction
  | AnalyticsPanelViewsAction
  | AnalyticsPanelExportsAction
  | AnalyticsPanelDetailsAction;

/*
 * Helpers.
 */

function calculateSelectedNamespace(
  teams: TeamModel[],
  userNamespace: string,
  currentNamespace: string | undefined,
  isUserGlobalAdmin: boolean,
): {id?: string; namespace: AnalyticsParametersNamespacesEnum} {
  if (isUserGlobalAdmin && currentNamespace && computeIsGlobalNamespace(currentNamespace)) {
    return {namespace: AnalyticsParametersNamespacesEnum.GLOBAL};
  }

  const defaultNamespace = teams.length > 0 ? teams[0].namespace : userNamespace;

  if (!currentNamespace) {
    if (teams.length > 0) {
      return {namespace: AnalyticsParametersNamespacesEnum.TEAM_REPORT, id: defaultNamespace};
    }
    return {namespace: AnalyticsParametersNamespacesEnum.TEAMMATE_REPORT, id: defaultNamespace};
  }

  // If teamId is defined in the URL, we're using it to auto select the team (if found) instead of
  // using the first one in teams.
  const teamNamespaces = teams.map((team) => team.namespace);
  const selectedNamespace = teamNamespaces.includes(currentNamespace) ? currentNamespace : defaultNamespace;

  return {namespace: AnalyticsParametersNamespacesEnum.TEAM_REPORT, id: selectedNamespace};
}

function buildDefaultParameters(state: AppState, location: AnalyticsLocation): AnalyticsParameters {
  const {namespace, reportType, selectedViewId} = parseInfoFromLocation(location);
  const userNamespace = getUserNamespace(state);
  const isUserGlobalAdmin = getIsUserGlobalAdmin(state);
  const teams = getTeamsAccessibleForAnalytics(state);

  const selectedNamespace = calculateSelectedNamespace(teams, userNamespace, namespace, isUserGlobalAdmin);

  // Default time period is last rolling 7 days (ending yesterday 23:59:59.999).
  const todayStartOfDay = DateTime.local();
  const defaultStartTime = todayStartOfDay.minus({day: 7}).startOf('day').toMillis();
  const defaultEndTime = todayStartOfDay.minus({day: 1}).endOf('day').toMillis();

  const parameters = {
    reportType: reportType ?? AnalyticsReportsEnum.OVERVIEW,
    query: {
      from: defaultStartTime,
      to: defaultEndTime,
      selectedViewId,
    },
  };

  if (selectedNamespace.namespace === AnalyticsParametersNamespacesEnum.TEAM_REPORT && selectedNamespace.id) {
    return {
      ...parameters,
      namespace: AnalyticsParametersNamespacesEnum.TEAM_REPORT,
      teamId: selectedNamespace.id,
      location: {
        pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}/team/${selectedNamespace.id}/${reportType}`,
        search: `?from=${defaultStartTime}&to=${defaultEndTime}`,
      },
    };
  }

  if (
    selectedNamespace.namespace === AnalyticsParametersNamespacesEnum.TEAMMATE_REPORT &&
    selectedNamespace.id
  ) {
    return {
      ...parameters,
      namespace: AnalyticsParametersNamespacesEnum.TEAMMATE_REPORT,
      teammateId: selectedNamespace.id,
      location: {
        pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}/team/${selectedNamespace.id}/${reportType}`,
        search: `?from=${defaultStartTime}&to=${defaultEndTime}`,
      },
    };
  }

  return {
    ...parameters,
    namespace: AnalyticsParametersNamespacesEnum.GLOBAL,
    location: {
      pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}/global/${reportType}`,
      search: `?from=${defaultStartTime}&to=${defaultEndTime}`,
    },
  };
}
